// src/pages/creator-panel/CallLogs/components/JsonViewer.js
// Tiny recursive collapsible JSON renderer. No external deps.
// Used in both the Analysis tab and expanded log rows.
import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

// Syntax-highlight palette tuned for sufficient contrast on a light background
// (the project's default theme). MUI does not have a built-in code-token
// palette so these are hand-picked Tailwind 600/700-range values.
const COLORS = {
    key: '#0369a1',     // sky-700
    string: '#15803d',  // green-700
    number: '#b45309',  // amber-700
    boolean: '#be185d', // pink-700
    null: '#64748b',    // slate-500
    bracket: '#475569', // slate-600
};

const renderPrimitive = (value) => {
    if (value === null) return <span style={{ color: COLORS.null }}>null</span>;
    if (typeof value === 'string') return <span style={{ color: COLORS.string }}>"{value}"</span>;
    if (typeof value === 'number') return <span style={{ color: COLORS.number }}>{value}</span>;
    if (typeof value === 'boolean') return <span style={{ color: COLORS.boolean }}>{String(value)}</span>;
    return <span>{String(value)}</span>;
};

const Node = ({ name, value, depth = 0, initiallyExpanded = true }) => {
    const [open, setOpen] = useState(initiallyExpanded);

    const indent = { pl: depth * 1.5 };

    if (value === null || typeof value !== 'object') {
        return (
            <Box sx={{ ...indent, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                {name !== undefined && (
                    <span style={{ color: COLORS.key }}>{`"${name}"`}</span>
                )}
                {name !== undefined && <span style={{ color: COLORS.bracket }}>: </span>}
                {renderPrimitive(value)}
            </Box>
        );
    }

    const isArray = Array.isArray(value);
    const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
    const open_bracket = isArray ? '[' : '{';
    const close_bracket = isArray ? ']' : '}';

    if (entries.length === 0) {
        return (
            <Box sx={{ ...indent, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
                {name !== undefined && (
                    <>
                        <span style={{ color: COLORS.key }}>{`"${name}"`}</span>
                        <span style={{ color: COLORS.bracket }}>: </span>
                    </>
                )}
                <span style={{ color: COLORS.bracket }}>{open_bracket}{close_bracket}</span>
            </Box>
        );
    }

    return (
        <Box sx={{ ...indent, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: 0.25, mr: 0.5, color: 'text.secondary' }}>
                    {open ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
                </IconButton>
                {name !== undefined && (
                    <>
                        <span style={{ color: COLORS.key }}>{`"${name}"`}</span>
                        <span style={{ color: COLORS.bracket }}>: </span>
                    </>
                )}
                <span style={{ color: COLORS.bracket }}>{open_bracket}</span>
                {!open && (
                    <Typography component="span" sx={{ color: 'text.disabled', ml: 0.5, fontStyle: 'italic', fontSize: 12 }}>
                        {entries.length} {entries.length === 1 ? 'item' : 'items'}
                    </Typography>
                )}
                {!open && <span style={{ color: COLORS.bracket }}>{close_bracket}</span>}
            </Box>
            {open && (
                <>
                    <Box sx={{ pl: 2 }}>
                        {entries.map(([k, v]) => (
                            <Node key={k} name={isArray ? undefined : k} value={v} depth={0} initiallyExpanded={depth < 1} />
                        ))}
                    </Box>
                    <Box sx={{ pl: 0.5 }}>
                        <span style={{ color: COLORS.bracket }}>{close_bracket}</span>
                    </Box>
                </>
            )}
        </Box>
    );
};

const JsonViewer = ({ value, name }) => {
    if (value === null || value === undefined) {
        return <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>No data</Typography>;
    }
    return (
        <Box sx={{
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            overflowX: 'auto',
        }}>
            <Node name={name} value={value} depth={0} initiallyExpanded={true} />
        </Box>
    );
};

export default JsonViewer;
